<template>
  <div class="ma-4">
    <!-- navigation -->
    <top-navigation :route-name="'events'" />

    <v-row v-if="ready" class="justify-center no-gutters">
      <v-col class="col-12 pl-3">
        <v-alert v-show="!event.active" type="warning" color="error" dense>
          {{ $t('singleEventView.eventIsUnpublished') }}
        </v-alert>
      </v-col>

      <!-- description -->
      <v-col class="col-6 pt-0">
        <v-row>
          <!-- translations -->
          <v-col class="col-12 pt-0">
            <v-card outlined>
              <v-card-title>
                {{ $t('singleEventView.presentEvent') }}
              </v-card-title>
              <v-card-text class="pb-1">
                <!-- title, lead, description and language -->
                <translations-form
                  ref="translationForm"
                  :entity-translations="event.getEntityTranslations()"
                />
              </v-card-text>
            </v-card>
          </v-col>

          <v-col class="col-12 pt-0">
            <v-card outlined>
              <v-card-title>
                {{ $t('singleEventView.eventData') }}
              </v-card-title>
              <v-card-text>
                <v-form ref="eventForm" @submit.prevent="">
                  <v-row>
                    <!-- select event template -->
                    <v-col class="col-6">
                      <v-select
                        v-model="event.productId"
                        outlined
                        :disabled="editMode"
                        :items="eventProducts.events"
                        :label="$t('singleEventView.eventTemplate')"
                        :rules="[
                          (v) =>
                            !!v || $t('singleEventView.selectEventTemplate'),
                        ]"
                        :item-text="
                          (item) => {
                            return this.$t(item.translation)
                          }
                        "
                        :item-value="
                          (item) => {
                            return item.id
                          }
                        "
                      />
                    </v-col>

                    <!-- Date -->
                    <v-col class="col-6">
                      <s-date-picker
                        :disabled="editMode"
                        :label="$t('singleEventView.eventDate')"
                        :rules="[
                          (v) => !!v || $t('singleEventView.selectEventDate'),
                        ]"
                        :date.sync="event.date"
                      />
                    </v-col>
                  </v-row>

                  <!-- time -->
                  <v-row>
                    <!-- from time -->
                    <v-col class="col-6">
                      <s-time-picker
                        :time.sync="event.startTime"
                        :show-clock="false"
                        :label="$t('singleEventView.eventStartTime')"
                        :rules="[
                          (v) =>
                            !!v || $t('singleEventView.eventStartMandatory'),
                          (v) =>
                            (!!v &&
                              v.match(
                                /(([0][0-9])|([1][0-9])|([2][0-3]))(:[0-5][0-9])/
                              )) ||
                            $t('singleEventView.timeIncomplete'),
                        ]"
                      />
                    </v-col>

                    <!-- end time -->
                    <v-col class="col-6">
                      <s-time-picker
                        :time.sync="event.endTime"
                        :show-clock="false"
                        :label="$t('singleEventView.eventEndTime')"
                        :rules="[
                          (v) => !!v || $t('singleEventView.eventEndMandatory'),
                          (v) =>
                            (!!v &&
                              v.match(
                                /(([0][0-9])|([1][0-9])|([2][0-3]))(:[0-5][0-9])/
                              )) ||
                            $t('singleEventView.timeIncomplete'),
                        ]"
                      />
                    </v-col>
                  </v-row>
                </v-form>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>

      <!-- translations, buttons and image -->
      <v-col class="col-6">
        <!-- image -->
        <v-row v-if="editMode">
          <v-col class="col-12 px-0 pt-0">
            <v-card outlined>
              <v-card-title>
                {{ $t('singleEventView.eventImage') }}
              </v-card-title>
              <v-card-text>
                <image-uploader
                  :initial-image-link="eventImgLink"
                  :label="$t('singleEventView.selectEventImage')"
                  @uploaded="fileUploadedHandler"
                  @cleared="fileCleared"
                />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- capacity and pricing buttons -->
        <v-row v-if="editMode">
          <v-col class="col-12 px-0 pt-0">
            <v-card outlined>
              <v-card-text>
                <v-row>
                  <!-- capacity management -->
                  <v-col class="col-8">
                    <v-btn large color="primary" @click="goToProduct()">
                      <span class="fal fa-warehouse-alt mr-2" />
                      {{ $t('productCapacity.editCapacity') }}
                    </v-btn>
                  </v-col>

                  <!-- pricing -->
                  <v-col class="col-4">
                    <v-btn large color="primary" @click="overridePrice()">
                      <span class="fal fa-chart-line mr-2" />
                      {{ $t('singleEventView.editPricing') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- buttons -->
        <v-row>
          <v-col class="col-12 px-0 pt-0">
            <v-card outlined>
              <v-card-text>
                <v-row class="justify-end">
                  <!-- publish / unpublish buttons -->
                  <v-col v-if="editMode" class="col-8 text-left">
                    <v-btn
                      v-if="event.active"
                      large
                      color="warning"
                      @click="unpublishEvent()"
                    >
                      <span class="fal fa-eye-slash mr-2" />
                      {{ $t('singleEventView.removeEventFromShop') }}
                    </v-btn>

                    <v-btn
                      v-else
                      large
                      class="white--text green"
                      @click="publishEvent()"
                    >
                      <span class="fal fa-eye mr-2" />
                      {{ $t('singleEventView.publishEvent') }}
                    </v-btn>
                  </v-col>

                  <!-- save button -->
                  <v-col class="col-4" :class="{ 'text-right': !editMode }">
                    <v-btn large color="primary" @click="saveEvent()">
                      <span class="fal fa-save mr-2"></span>
                      {{ $t('general.save') }}
                    </v-btn>
                  </v-col>

                  <v-col v-if="editMode" class="col-12">
                    <v-btn
                      :disabled="!isDeletableEvent()"
                      color="error"
                      large
                      @click="$refs.askToDeleteEvent.show()"
                    >
                      <span class="fal fa-times mr-2"></span>
                      {{ $t('singleEventView.deleteEvent') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
    <!-- ask to really change the event's time -->
    <s-modal
      ref="askToChangeTime"
      :ok-text="$t('singleEventView.overrideTime')"
      :header-text="$t('singleEventView.reallyOverrideTime')"
      :size="1"
      @ok="setOriginalTimeToCurrent()"
    >
      <v-row>
        <v-col class="col-12">
          <span class="fal fa-exclamation-triangle mr-2 red--text" />
          {{ $t('singleEventView.explainTimeChange') }}
        </v-col>
      </v-row>
    </s-modal>

    <!-- ask to really delete the event -->
    <s-modal
      ref="askToDeleteEvent"
      :size="2"
      :ok-text="$t('general.cancel')"
      :cancel-text="$t('general.delete')"
      cancel-icon="fa fa-trash mr-2"
      :header-text="$t('singleEventView.reallyDeleteHeader')"
      @cancel="deleteEvent()"
    >
      <v-row>
        <v-col>
          <span class="fal fa-exclamation-triangle mr-2 error--text" />
          {{ $t('singleEventView.reallyDeleteText') }}
        </v-col>
      </v-row>
    </s-modal>
  </div>
</template>

<script>
import SDatePicker from '../../../components-shared/utils/SDatePicker'
import Event from '../../../classes-shared/events/Event'
import EventProducts from '../../../classes-shared/events/EventProducts'
import STimePicker from '../../../components-shared/utils/STimePicker'
import TopNavigation from '../../../components-shared/menus/TopNavigation'
import ImageUploader from '../../products/ImageUploader'
import TranslationsForm from '../../products/TranslationsForm'
import moment from 'moment'
import { padStart } from 'lodash'

export default {
  name: 'SingleEventView',
  components: {
    TranslationsForm,
    ImageUploader,
    TopNavigation,
    STimePicker,
    SDatePicker,
  },

  data() {
    return {
      eventProducts: new EventProducts(),

      // Type: Event
      event: null,
      eventImgLink: '',
      capacityToOverride: null,
      product: null,
      prodCapacity: null,
      prodCapacityValue: null,
      originalEventStartTime: null,
      originalEventEndTime: null,
    }
  },

  computed: {
    editMode() {
      return this.$route.params.id > 0
    },

    createDate() {
      return this.$route.params.date
    },

    // short cut to decide whether the form can be rendered
    ready() {
      return this.eventProducts && this.event
    },

    // short cut
    assets() {
      if (this.event) return this.event.getAssets()
      return []
    },
  },

  async mounted() {
    // load possible products as event templates
    await this.eventProducts.loadProducts()

    // load event
    await this.loadEvent()
    this.eventImgLink = this.getEventImageLink()
  },

  methods: {
    goToProduct() {
      this.$router.push({
        name: 'product',
        params: { id: this.event.productId },
      })
    },
    // go to pricing dashboard frontend to override price of this event
    overridePrice() {
      const pricingDashboardUrl =
        process.env.VUE_APP_PRICING_DASHBOARD_URL +
        '/backend/pricing/event/price'
      const queryParams = `productId=${this.event.productId}&eventId=${this.event.id}&start=${this.event.date}&end=${this.event.date}`
      window.open(`${pricingDashboardUrl}?${queryParams}`, '_blank')
    },

    // create or update event
    async saveEvent() {
      // does the original and current event times differ? Ask user to really change them
      if (!this.checkEventTime()) return

      // check form to be complete
      if (!this.validate()) return

      // update existing event
      if (this.editMode) {
        await this.event.updateEvent()
      } else {
        // create new event
        await this.event.createEvent()

        // route to edit event
        this.$router.push({
          name: 'editEvent',
          params: { id: this.event.id, event: this.event },
        })
      }
    },

    // prevent user from unintentionally change the event's time
    checkEventTime() {
      if (
        this.originalEventEndTime === null &&
        this.originalEventStartTime === null
      ) {
        return true
      }
      if (
        this.originalEventStartTime &&
        this.event.startTime &&
        this.originalEventEndTime === this.event.endTime
      ) {
        return true
      } else {
        this.$refs.askToChangeTime.show()
        return false
      }
    },
    setOriginalTimeToCurrent() {
      this.originalEventStartTime = this.event.startTime
      this.originalEventEndTime = this.event.endTime
      this.saveEvent()
    },

    publishEvent() {
      if (!this.validate()) return
      this.event.publishEvent()
    },

    unpublishEvent() {
      if (!this.validate()) return
      this.event.unpublishEvent()
    },

    async deleteEvent() {
      if (await this.event.delete()) {
        this.$router.push({ name: 'events' })
      }
    },

    // shortcut to validate all forms
    validate() {
      return (
        this.$refs.translationForm.validate() && this.$refs.eventForm.validate()
      )
    },

    // responsible method for (re-)loading the event
    async loadEvent() {
      // existing event
      if (this.editMode) {
        // if entire event instance was passed after creating a new event
        let tmpEvent = new Event({ id: this.$route.params.id })
        await tmpEvent.loadEvent()
        this.event = tmpEvent

        if (typeof this.event.startTime === 'string') {
          this.event.startTime = padStart(this.event.startTime, 5, '0')
        }
        if (typeof this.event.endTime === 'string') {
          this.event.endTime = padStart(this.event.endTime, 5, '0')
        }

        // temporarily save original event time to ask user really to change the time
        this.originalEventStartTime = this.event.startTime
        this.originalEventEndTime = this.event.endTime
      } else {
        this.event = new Event({})
        // set date picker
        this.event.date = this.createDate
      }
      await this.fetchCapacity()
    },

    getEventImageLink() {
      const assetsLength = this.assets.length
      if (assetsLength > 0) {
        // events have only one image
        return this.assets[assetsLength - 1].fileResource.getImageUrl()
      }

      return null
    },

    async fetchCapacity() {
      this.product = this.eventProducts.findProductById(this.event.productId)
      if (this.product) {
        this.prodCapacity = await this.product.fetchCapacities(
          moment(this.event.date),
          moment(this.event.date)
        )
        this.prodCapacityValue = this.prodCapacity.getCapacityValues()[0]
        if (this.prodCapacityValue) {
          this.capacityToOverride = this.prodCapacityValue
        }
      }
    },

    async overrideCapacity() {
      if (this.prodCapacityValue && this.$refs.overrideForm.validate()) {
        this.prodCapacityValue = await this.prodCapacityValue.overrideCapacityAmount(
          this.product,
          moment(this.event.date).format('YYYY-MM-DD'),
          moment(this.event.date).format('YYYY-MM-DD'),
          this.capacityToOverride.getCapacity()
        )
      }
    },

    async fileUploadedHandler(uploadedFile) {
      if (uploadedFile) {
        this.event
          .getExtendedAttribute()
          .parseApiAssets([
            { id: uploadedFile.id, order: 1, fileResource: uploadedFile },
          ])
        await this.event.updateEvent()
        this.eventImgLink = await this.getEventImageLink()
      }
    },

    fileCleared() {
      this.event.getExtendedAttribute().assets = []
      this.event.updateEvent()
    },

    isDeletableEvent() {
      return !this.event.active && this.prodCapacityValue?.count === 0
    },
  },
}
</script>
